import { DataModule } from "./DataModule.js";
import { Services } from "../../services/Services.js";
import { isFunction } from "../../data/utils.js";
import { i18n } from "../../globalization/i18n.js";
import { DataEditorControl } from "../controls/data/DataEditorControl.js";
import * as editorHelpers from "../utils/data-editor-helpers.js";

export class DataEditor extends DataModule {

    static MODE_READ = 'read';
    static MODE_CREATE = 'create';
    static MODE_UPDATE = 'update';

    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: () => this.getDataModel().getEntityName() + ':Editor',
            moduleTitle: () => this.getDataModel().getLabel(),
        }, options || {});

        super(parent, options);

        this.browserRouteName = options.browserRouteName ?? null;
        this.editorRouteName = options.editorRouteName ?? null;
        this.editorDefs = options.editorDefs ?? null;
        this.editorFields = options.editorFields ?? null;
        this.resolvedEditorDefs = null;
        this.viewMode = DataEditor.MODE_READ;
        this.editorControl = null;

    };

    getViewMode() {

        return this.viewMode;

    };

    setViewMode(mode) {

        this.viewMode = mode;
        return mode;

    };

    changeViewMode(mode, invalidate = true) {

        this.setViewMode(mode);
        if (invalidate)
            this.invalidate();

    };

    validateData() {

        const datamodel = this.getDataModel();
        const data = this.getData();

        if (datamodel && !datamodel.validateData(data))
            return false;

        return this.getControls().every((control) => {

            if (!control)
                return true;
            if (control.validateData && typeof control.validateData == 'function')
                return control.validateData();
            if (control.validateValue && typeof control.validateValue == 'function')
                return control.validateValue();

            return true;

        });

    };

    // --------------------------------------------------------------------------------------------

    isDataChanged() {

        const data = this.getData();
        return !!(data && data.$changed);

    };

    resolveDataParams(options = {}, activeRoute = null) {

        return Object.assign({}, activeRoute?.params ?? {}, options ?? {});

    };

    isNewDataRecord(data) {

        const id = data?.id;
        return id === 'new' || id === null || id === undefined || id === '';

    };

    getEditorControl() {

        return this.editorControl || null;

    };

    getConfiguredEditorDefs() {

        return typeof this.editorDefs == 'function'
            ? this.editorDefs.call(this)
            : this.editorDefs;

    };

    getConfiguredEditorFields() {

        return typeof this.editorFields == 'function'
            ? this.editorFields.call(this)
            : this.editorFields;

    };

    resolveEditorDefs() {

        const configuredEditorDefs = this.getConfiguredEditorDefs();
        if (configuredEditorDefs !== null && configuredEditorDefs !== undefined)
            return configuredEditorDefs;

        const configuredEditorFields = this.getConfiguredEditorFields();
        if (configuredEditorFields !== null && configuredEditorFields !== undefined)
            return editorHelpers.createEditorDefsFromFieldNames(this.getDataModel(), configuredEditorFields);

        return editorHelpers.createEditorDefsFromModel(this.getDataModel());

    };

    ensureEditorDefs(force = false) {

        if (!force && this.resolvedEditorDefs !== null)
            return this.resolvedEditorDefs;

        this.resolvedEditorDefs = this.resolveEditorDefs() || [];
        return this.resolvedEditorDefs;

    };

    getEditorDefs() {

        return this.ensureEditorDefs();

    };

    createDefaultEditorControl() {

        return new DataEditorControl(this, {
            data: () => this.getData(),
            dataModel: () => this.getDataModel(),
            editorDefs: () => this.getEditorDefs(),
            readOnly: () => !this.canEditData(),
            canUserEditField: () => this.canEditData(),
            onFieldValueChange: (value, field, data) => {
                if (!data || !field)
                    return false;

                field.setValue(data, value);
                data.$changed = true;
                this.invalidate();
                return true;
            }
        });

    };

    ensureDefaultEditorControl() {

        if (this.getEditorControl())
            return this.getEditorControl();
        if (this.getControls().length > 0)
            return null;

        this.editorControl = this.createDefaultEditorControl();
        return this.editorControl;

    };

    canEditData() {

        const data = this.getData();

        if (this.isNewDataRecord(data))
            return this.canUserCreateRecord();

        return this.canUserUpdateRecord();

    };

    closeEditor() {

        this.getApp().navigateBack();

    };

    async _prepare(options, activeRoute) {

        await super._prepare(options, activeRoute);
        this.ensureEditorDefs(true);

    };

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);
        this.ensureEditorDefs();

        const params = this.resolveDataParams(options, activeRoute);
        const mode = params.id === 'new' ? DataEditor.MODE_CREATE : DataEditor.MODE_UPDATE;

        if (!this.hasExternalDataBinding()) {
            const dataModel = this.getDataModel();
            const dataManager = this.getDataManager();
            const id = params?.id ?? this.getData()?.id ?? null;

            if (id === 'new') {
                const item = dataModel && typeof dataModel.createItem === 'function'
                    ? dataModel.createItem()
                    : {};
                item.id = 'new';
                this.setData(item);
            } else if (!id) {
                this.setData(null);
            } else {
                if (!dataManager)
                    throw new Error('[DataEditor] data manager is required.');

                const data = await dataManager.readData(id);
                this.setData(data);
            }
        }

        if (this.getControls().length == 0)
            this.ensureDefaultEditorControl();

        this.setViewMode(mode);

    };

    async loadData(options = {}) {

        this.getApp().setBusyFlag();
        try {
            await this.refresh(options);
            this.invalidate();
            return this.getData();
        } catch (error) {
            this.handleError(error);
            return null;
        } finally {
            this.getApp().clearBusyFlag();
        }

    };

    async saveData() {

        const data = this.getData();
        if (!data) {
            this.getLogger().error(i18n.t('err.missing_data.label'));
            return false;
        }

        this.getApp().setBusyFlag();
        try {
            if (this.isNewDataRecord(data))
                await this.createData();
            else
                await this.updateData();

            await this.updateDependentData();
            this.setViewMode(this.isNewDataRecord(this.getData()) ? DataEditor.MODE_CREATE : DataEditor.MODE_UPDATE);
            this.invalidate();
            return true;
        } catch (error) {
            this.handleError(error);
            return false;
        } finally {
            this.getApp().clearBusyFlag();
        }

    };

    async createData() {

        const dataManager = this.getDataManager();
        if (!dataManager)
            throw new Error('[DataEditor] data manager is required.');

        const createdData = await dataManager.createData(this.getData());
        if (createdData && typeof createdData == 'object') {
            this.setData(createdData);
        } else {
            const id = createdData;
            if (id === undefined || id === null || id === '')
                throw new Error('[DataEditor] created data id is missing.');

            this.setData(Object.assign({}, this.getData(), { id }));
        }

        await this.createDependentData();

        return this.getData();

    };

    async updateData() {

        const dataManager = this.getDataManager();
        if (!dataManager)
            throw new Error('[DataEditor] data manager is required.');

        const updatedData = await dataManager.updateData(this.getData());
        if (updatedData)
            this.setData(updatedData);

        return updatedData ?? this.getData();

    };

    async deleteData() {

        const data = this.getData();
        if (!data) {
            this.getLogger().error(i18n.t('err.missing_data.label'));
            return false;
        }

        const dataManager = this.getDataManager();
        if (!dataManager)
            throw new Error('[DataEditor] data manager is required.');

        this.getApp().setBusyFlag();
        try {
            await this.deleteDependentData();
            await dataManager.deleteData(data);
            this.setData(null);
            this.invalidate();
            return true;
        } catch (error) {
            this.handleError(error);
            return false;
        } finally {
            this.getApp().clearBusyFlag();
        }

    };

    async createDependentData() {

        return true;

    };

    async updateDependentData() {

        return true;

    };

    async deleteDependentData() {

        return true;

    };

    // --------------------------------------------------------------------------------------------

    changeDataProp(propName, value, notify = true, invalidate = true) {

        const data = this.getData();

        if (!data) return;

        const priorValue = data[propName];

        if (priorValue === value)
            return;

        data[propName] = value;
        data.$changed = true;

        if (invalidate)
            this.invalidate();
        if (notify)
            this.dataChanged(propName, value, priorValue);

    };

    dataChanged(prop, value, priorValue) {

        if (!this.onDataChange)
            return;
        if (!isFunction(this.onDataChange))
            return;

        this.onDataChange.call(this, { prop, value, priorValue });

    }

    // --------------------------------------------------------------------------------------------

    _render(vm) {

        const ct = Services.get('templates');

        return ct.module([
            ct.header(ct.concat([
                this.renderModuleNav(),
            ])),
            ct.section([
                this.renderContent(vm),
            ]),
        ]);

    };

    renderModuleNav() {

        const ct = Services.get('templates');

        if (this.getModuleMode() == DataModule.MODE_CONTROL)
            return null;

        const data = this.getData();
        const title = data ? (data.displayLabel ?? data.name) : '?';

        return [
            ct.nav('.m.l', [
                ct.iconButton('back', '.transparent.circle', {
                    title: i18n.t('button.back.label'),
                    onclick: () => { this.closeEditor(); }
                }),
                ct.iconButton('refresh', '.transparent.circle', {
                    title: i18n.t('button.refresh.label'),
                    onclick: () => { this.loadData(); }
                }),
                ct.h2([title]),
                ct.navgap(),
                ct.button(i18n.t('button.delete.label'), 'delete', '.outlined', {
                    onclick: () => { this.deleteData(); },
                    visible: this.canUserDeleteRecord() && !this.isNewDataRecord(this.getData()),
                }),
                ct.button(i18n.t('button.save.label'), 'save', {
                    onclick: () => { this.saveData(); },
                    disabled: !this.isDataChanged(),
                    visible: (
                        (this.isNewDataRecord(this.getData()) && this.canUserCreateRecord())
                        || (!this.isNewDataRecord(this.getData()) && this.canUserUpdateRecord())
                    ),
                }),
            ]),
            ct.nav('.s', [
                ct.iconButton('back', '.circle.transparent', {
                    title: i18n.t('button.back.label'),
                    onclick: () => { this.closeEditor(); }
                }),
                ct.iconButton('refresh', '.circle.transparent', {
                    title: i18n.t('button.refresh.label'),
                    onclick: () => { this.loadData(); }
                }),
                ct.navgap(),
                ct.iconButton('delete', '.outlined.circle', {
                    title: i18n.t('button.delete.label'),
                    onclick: () => { this.deleteData(); },
                    visible: this.canUserDeleteRecord() && !this.isNewDataRecord(this.getData()),
                }),
                ct.iconButton('save', '.circle', {
                    title: i18n.t('button.save.label'),
                    onclick: () => { this.saveData(); },
                    disabled: !this.isDataChanged(),
                    visible: (
                        (this.isNewDataRecord(this.getData()) && this.canUserCreateRecord())
                        || (!this.isNewDataRecord(this.getData()) && this.canUserUpdateRecord())
                    ),
                }),
            ]),
        ];

    };

    renderContent(vm) {

        const editorControl = this.getEditorControl();

        if (!editorControl)
            return null;

        return editorControl.render(vm);

    };

}
